import { children, countNodes, depth } from './ast.js';
import { FormulaErrorCodes, formulaError } from './errors.js';
import { DEFAULT_MAX_NESTING } from './parser.js';
const ALL_BINARY_OPERATORS = ['+', '-', '*', '/', '==', '!=', '<', '<=', '>', '>=', '&&', '||'];
/** What a body inside an aggregate may combine its row's fields with: every operator but division. */
const AGGREGATE_BODY_OPERATORS = ALL_BINARY_OPERATORS.filter((op) => op !== '/');
/** The FL-E05 reason of a division inside an aggregate (Computed-Fields.md §8.3). */
const AGGREGATE_DIVISION_REASON = "The saved rows' quotients would be computed by the database and the edited rows' in memory, at different " +
    'precision, so the stored total would depend on which row was edited. Compute the quotient per row in a ' +
    'computed field of the child and aggregate that field.';
/**
 * A host's restriction of the language (§4.8): which constructs it accepts and how large a formula may be.
 * Profiles are data, not subclasses — a host restricts the one language instead of forking it.
 */
export class FormulaProfile {
    constructor(definition) {
        this.definition = definition;
    }
    get name() {
        return this.definition.name;
    }
    static byName(name) {
        return FormulaProfile.all.find((p) => p.name === name);
    }
    /** How deep a text may nest while it is parsed (§8): the profile's depth limit, or the language's default without one. */
    get maxNesting() {
        return this.definition.maxDepth ?? DEFAULT_MAX_NESTING;
    }
    /** FL-E09 for a text over the profile's length, spanning the whole text; checked before the text is parsed. */
    checkLength(text) {
        const errors = [];
        checkLimit(errors, [0, text.length], 'length', this.definition.maxLength, text.length);
        return errors[0] ?? null;
    }
    /** True when the profile lets formulas call `fn`; whether it exists is the registry's question. */
    allowsFunction(fn) {
        const { functions, excludedFunctions } = this.definition;
        return functions !== null ? functions.includes(fn) : !excludedFunctions.includes(fn);
    }
    /** FL-E05 for every construct outside the profile, FL-E09 for a formula over its limits (at the root). */
    check(root, textLength) {
        const errors = [];
        checkLimit(errors, root.span, 'length', this.definition.maxLength, textLength);
        checkLimit(errors, root.span, 'depth', this.definition.maxDepth, depth(root));
        checkLimit(errors, root.span, 'nodes', this.definition.maxNodes, countNodes(root));
        this.checkConstructs(root, errors);
        return errors;
    }
    checkConstructs(node, errors) {
        const construct = this.disallowed(node);
        if (construct !== null) {
            errors.push(formulaError(FormulaErrorCodes.NotAllowedInProfile, node.span, this.notAllowedMessage(construct), { construct }));
        }
        for (const child of children(node))
            this.checkConstructs(child, errors);
    }
    /** FL-E05's message: what is not allowed where, and — where the profile gives one — why, and what to write instead. */
    notAllowedMessage(construct) {
        const message = `'${construct}' is not allowed ${this.definition.context}.`;
        const reason = this.definition.reasons?.[construct];
        return reason ? `${message} ${reason}` : message;
    }
    /** The name of the construct `node` uses when the profile forbids it; null when allowed. */
    disallowed(node) {
        switch (node.kind) {
            case 'str':
            case 'bool':
            case 'null':
                return this.disallowedLiteral(node.kind);
            case 'unary':
                return this.definition.unaryOperators.includes(node.op) ? null : `unary ${node.op}`;
            case 'binary':
                return this.definition.binaryOperators.includes(node.op) ? null : node.op;
            case 'call':
                return this.allowsFunction(node.fn) ? null : `${node.fn}()`;
            default:
                return this.disallowedCombinator(node.kind);
        }
    }
    disallowedLiteral(kind) {
        const d = this.definition;
        const literals = {
            str: [d.allowsStringLiterals, 'string literal'],
            bool: [d.allowsBooleanLiterals, 'boolean literal'],
            null: [d.allowsNullLiteral, 'null literal'],
        };
        const [allowed, name] = literals[kind];
        return allowed ? null : name;
    }
    disallowedCombinator(kind) {
        if (kind === 'coalesce')
            return this.definition.allowsCoalesce ? null : '??';
        if (kind === 'cond')
            return this.definition.allowsConditional ? null : '?:';
        return null;
    }
}
/**
 * Statement rows (§10): row references, numbers, binary + and -, parentheses. No unary minus, no limits —
 * exactly the language StatementFormula accepts.
 */
FormulaProfile.statement = new FormulaProfile({
    name: 'statement',
    context: 'in the statement profile',
    binaryOperators: ['+', '-'],
    unaryOperators: [],
    allowsConditional: false,
    allowsCoalesce: false,
    allowsStringLiterals: false,
    allowsBooleanLiterals: false,
    allowsNullLiteral: false,
    functions: [],
    excludedFunctions: [],
    maxLength: null,
    maxDepth: null,
    maxNodes: null,
});
/**
 * PlanView measures (§4.8): measure references, numbers, every operator, `??`, `?:` and the row functions
 * `round`, `abs`, `min`, `max` and `if` — what the cell aggregation of §9.3 totals correctly. A string literal
 * is allowed for `round()`'s mode, the one place a measure formula can use one; Boolean and null literals are
 * not. The planning functions arrive in Phase 4.
 */
FormulaProfile.measure = new FormulaProfile({
    name: 'measure',
    context: 'in the measure profile',
    binaryOperators: ALL_BINARY_OPERATORS,
    unaryOperators: ['-', '!'],
    allowsConditional: true,
    allowsCoalesce: true,
    allowsStringLiterals: true,
    allowsBooleanLiterals: false,
    allowsNullLiteral: false,
    functions: ['round', 'abs', 'min', 'max', 'if'],
    excludedFunctions: [],
    maxLength: 1024,
    maxDepth: 32,
    maxNodes: 200,
});
/** Entity computed fields: everything except the planning functions. The aggregate functions arrive with Phase 3b. */
FormulaProfile.entity = new FormulaProfile({
    name: 'entity',
    context: 'in the entity profile',
    binaryOperators: ALL_BINARY_OPERATORS,
    unaryOperators: ['-', '!'],
    allowsConditional: true,
    allowsCoalesce: true,
    allowsStringLiterals: true,
    allowsBooleanLiterals: true,
    allowsNullLiteral: true,
    functions: null,
    excludedFunctions: ['prev', 'ytd'],
    maxLength: null,
    maxDepth: 64,
    maxNodes: 500,
});
/**
 * What an aggregate's expression and `where` may contain (§4.7): child fields, literals, `+`, `-`, `*`,
 * comparisons, logical operators, `??` and `?:` — the subset that translates to SQL and computes the same there
 * as in memory. No function at all: `round()` would round differently in PostgreSQL (ties away from zero), and an
 * aggregate cannot nest. No division: the saved rows' quotients would carry PostgreSQL's scale and the tracked
 * rows' 28 significant digits, so the stored total would depend on which row a save changed — the quotient is a
 * row formula of the child, and the aggregate reads that field. Not a host's profile, so `byName` does not find
 * it; the resolver applies it to every body it opens a collection scope for.
 */
FormulaProfile.aggregateBody = new FormulaProfile({
    name: 'aggregate-body',
    context: 'inside an aggregate',
    binaryOperators: AGGREGATE_BODY_OPERATORS,
    unaryOperators: ['-', '!'],
    allowsConditional: true,
    allowsCoalesce: true,
    allowsStringLiterals: true,
    allowsBooleanLiterals: true,
    allowsNullLiteral: true,
    functions: [],
    excludedFunctions: [],
    maxLength: null,
    maxDepth: null,
    maxNodes: null,
    reasons: { '/': AGGREGATE_DIVISION_REASON },
});
FormulaProfile.all = [FormulaProfile.statement, FormulaProfile.measure, FormulaProfile.entity];
function checkLimit(errors, span, limit, max, actual) {
    if (max !== null && actual !== null && actual > max) {
        errors.push(formulaError(FormulaErrorCodes.LimitExceeded, span, `The formula's ${limit} is ${actual}; the profile allows at most ${max}.`, {
            limit,
            max: String(max),
            actual: String(actual),
        }));
    }
}
